// Newsletter image block.
//
// Three-node schema (forced by ProseMirror's renderSpec rule that the content
// hole `0` must be the only child of its parent array):
//
//   newsletterImage       content: "newsletterImageMedia imageCaption?"
//   newsletterImageMedia  atom, attrs: src, alt  → renders <img>
//   imageCaption          content: "inline*"     → renders <figcaption>
//
// newsletterImage renderHTML is just `['figure', attrs, 0]`. PM serializes the
// children (media + optional caption) using their own renderHTMLs.
//
// NodeView:
//   <figure data-newsletter-image data-align data-width>
//     <div class="nl-image__content">  ← contentDOM (PM renders children here)
//       <img>                            (newsletterImageMedia)
//       <figcaption>caption…             (imageCaption, when present)
//     </div>
//     <button> + caption                ← chrome, contenteditable=false

import { Node, mergeAttributes } from "@tiptap/core"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"

import { idAttributeSpec } from "./attributes"
import { deleteBlockAt, runStructuralCommand } from "./commands"

const VALID_ALIGN = ["left", "center", "right"]
const LEGACY_WIDTH = { small: 35, medium: 60, full: 100 }
export const IMAGE_WIDTH_MIN = 15
export const IMAGE_WIDTH_MAX = 100

export function normalizeImageWidth(raw, fallback = 100) {
  if (raw === null || raw === undefined || raw === "") return fallback
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return clamp(Math.round(raw), IMAGE_WIDTH_MIN, IMAGE_WIDTH_MAX)
  }
  const str = String(raw)
  if (LEGACY_WIDTH[str] != null) return LEGACY_WIDTH[str]
  const n = parseInt(str, 10)
  return Number.isFinite(n) ? clamp(n, IMAGE_WIDTH_MIN, IMAGE_WIDTH_MAX) : fallback
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

// Caption — only allowed inside newsletterImage (not in newsletterBlock group).
export const ImageCaption = Node.create({
  name: "imageCaption",
  content: "inline*",
  selectable: false,

  parseHTML() {
    return [{ tag: "figcaption" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["figcaption", mergeAttributes(HTMLAttributes), 0]
  },
})

// Media — atom holding the actual <img>. Not in newsletterBlock group; only
// valid as a child of newsletterImage.
export const NewsletterImageMedia = Node.create({
  name: "newsletterImageMedia",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (el) => el.getAttribute("src") || "",
        renderHTML: (attrs) => (attrs.src ? { src: attrs.src } : {}),
      },
      alt: {
        default: "",
        parseHTML: (el) => el.getAttribute("alt") || "",
        renderHTML: (attrs) => (attrs.alt ? { alt: attrs.alt } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: "img" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)]
  },

  // Image deletion via Backspace/Delete is prevented by StructuralDeleteGuard,
  // which blocks any NodeSelection on this node. Removal must go through the
  // ✕ button in the NodeView (single image) or the row-item delete button.
})

// Figure wrapper. Top-level block.
export const NewsletterImage = Node.create({
  name: "newsletterImage",
  group: "newsletterBlock",
  content: "newsletterImageMedia imageCaption?",
  draggable: true,
  selectable: true,
  isolating: true,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addAttributes() {
    return {
      id: idAttributeSpec,
      width: {
        default: 100,
        parseHTML: (el) => normalizeImageWidth(el.getAttribute("data-width")),
        renderHTML: (attrs) => ({ "data-width": String(normalizeImageWidth(attrs.width)) }),
      },
      align: {
        default: "center",
        parseHTML: (el) => {
          const value = el.getAttribute("data-align")
          return VALID_ALIGN.includes(value) ? value : "center"
        },
        renderHTML: (attrs) => ({ "data-align": attrs.align }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "figure[data-newsletter-image]" }]
  },

  renderHTML({ HTMLAttributes }) {
    const figureAttrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
      "data-newsletter-image": "",
    })
    return ["figure", figureAttrs, 0]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => buildImageView(node, editor, getPos)
  },

  addCommands() {
    return {
      insertNewsletterImage:
        ({ src = "", alt = "", width = "full", align = "center" } = {}) =>
        ({ state, dispatch, tr, view }) => {
          const imageType = state.schema.nodes.newsletterImage
          const mediaType = state.schema.nodes.newsletterImageMedia
          if (!imageType || !mediaType) return false

          const media = mediaType.create({ src, alt })
          const image = imageType.createAndFill({ width, align }, media)
          if (!image) return false

          const $from = state.selection.$from
          const insertPos = $from.depth === 0 ? $from.pos : $from.after(1)

          if (!dispatch) return true

          tr.insert(insertPos, image)
          tr.setSelection(NodeSelection.create(tr.doc, insertPos))
          dispatch(tr.scrollIntoView())
          view?.focus()
          return true
        },

      toggleImageCaption:
        (imagePos) =>
        ({ state, dispatch, tr }) => {
          let pos = imagePos
          if (pos === undefined || pos === null) {
            const sel = state.selection
            if (sel instanceof NodeSelection && sel.node.type.name === "newsletterImage") {
              pos = sel.from
            }
          }
          if (pos === undefined || pos === null) return false

          const image = state.doc.nodeAt(pos)
          if (!image || image.type.name !== "newsletterImage") return false

          const media = image.firstChild
          if (!media) return false
          const captionPos = pos + 1 + media.nodeSize

          if (!dispatch) return true

          if (image.childCount > 1) {
            const captionNode = image.child(1)
            tr.delete(captionPos, captionPos + captionNode.nodeSize)
          } else {
            const captionNode = state.schema.nodes.imageCaption.create()
            tr.insert(captionPos, captionNode)
            tr.setSelection(TextSelection.near(tr.doc.resolve(captionPos + 1)))
          }
          dispatch(tr)
          return true
        },

      setImageAttrs:
        (imagePos, attrs = {}) =>
        ({ state, dispatch, tr }) => {
          const image = state.doc.nodeAt(imagePos)
          if (!image || image.type.name !== "newsletterImage") return false
          const media = image.firstChild
          if (!media) return false

          if (!dispatch) return true

          const { src, alt, width, align, id } = attrs
          if (src !== undefined || alt !== undefined) {
            const mediaPos = imagePos + 1
            tr.setNodeMarkup(mediaPos, undefined, {
              ...media.attrs,
              ...(src !== undefined ? { src } : {}),
              ...(alt !== undefined ? { alt } : {}),
            })
          }
          if (width !== undefined || align !== undefined || id !== undefined) {
            tr.setNodeMarkup(imagePos, undefined, {
              ...image.attrs,
              ...(width !== undefined ? { width } : {}),
              ...(align !== undefined ? { align } : {}),
              ...(id !== undefined ? { id } : {}),
            })
          }
          dispatch(tr)
          return true
        },
    }
  },
})

function buildImageView(initialNode, editor, getPos) {
  const dom = document.createElement("figure")
  dom.setAttribute("data-newsletter-image", "")
  dom.className = "nl-image"

  const contentDOM = document.createElement("div")
  contentDOM.className = "nl-image__content"

  const captionToggle = document.createElement("button")
  captionToggle.type = "button"
  captionToggle.className = "nl-image__caption-toggle"
  captionToggle.textContent = "+ caption"
  captionToggle.contentEditable = "false"
  captionToggle.addEventListener("mousedown", (event) => event.preventDefault())
  captionToggle.addEventListener("click", (event) => {
    event.preventDefault()
    const pos = typeof getPos === "function" ? getPos() : null
    if (pos === null || pos === undefined) return
    editor.chain().focus().toggleImageCaption(pos).run()
  })

  const deleteBtn = document.createElement("button")
  deleteBtn.type = "button"
  deleteBtn.className = "nl-image__delete"
  deleteBtn.contentEditable = "false"
  deleteBtn.setAttribute("aria-label", "Delete image")
  deleteBtn.setAttribute("title", "Delete image")
  deleteBtn.innerHTML = `
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/>
    </svg>
  `
  deleteBtn.addEventListener("mousedown", (event) => event.preventDefault())
  deleteBtn.addEventListener("click", (event) => {
    event.preventDefault()
    const pos = typeof getPos === "function" ? getPos() : null
    if (pos === null || pos === undefined) return
    // Route through the shared command so the "doc never fully empty"
    // invariant in deleteBlockAt is enforced consistently with other
    // top-level deletes.
    runStructuralCommand(editor, deleteBlockAt(pos))
  })

  const sizeGroup = buildSizeGroup(
    [
      { value: 35,  label: "S", title: "Small" },
      { value: 60,  label: "M", title: "Medium" },
      { value: 100, label: "L", title: "Full width" },
    ],
    (width) => {
      const pos = typeof getPos === "function" ? getPos() : null
      if (pos === null || pos === undefined) return
      editor.chain().focus().setImageAttrs(pos, { width }).run()
    }
  )
  sizeGroup.classList.add("nl-image__size-group")

  const resizeHandle = buildResizeHandle({
    direction: "right",
    onResize: ({ delta, startWidth, pointerEvent }) => {
      const pos = typeof getPos === "function" ? getPos() : null
      if (pos === null || pos === undefined) return
      const container = dom.parentElement?.clientWidth || dom.clientWidth || 600
      const startPx = (startWidth / 100) * container
      // Center alignment: dragging right gains width on both sides, so the
      // perceived movement is doubled. Left/right align scales 1:1.
      const align = editor.state.doc.nodeAt(pos)?.attrs.align || "center"
      const scale = align === "center" ? 2 : 1
      const newPx = startPx + delta * scale
      const newPercent = Math.round((newPx / container) * 100)
      editor.chain().focus().setImageAttrs(pos, { width: newPercent }).run()
      pointerEvent?.preventDefault?.()
    },
    getStartWidth: () => normalizeImageWidth(editor.state.doc.nodeAt(getPos())?.attrs.width),
  })
  resizeHandle.classList.add("nl-image__resize-handle")

  dom.append(contentDOM, captionToggle, deleteBtn, sizeGroup, resizeHandle)

  const refresh = (node) => {
    const media = node.firstChild
    const hasSrc = !!media?.attrs.src
    const hasCaption = node.childCount > 1
    const width = normalizeImageWidth(node.attrs.width)
    dom.dataset.align = node.attrs.align || "center"
    dom.dataset.width = String(width)
    dom.style.setProperty("--nl-image-w", `${width}%`)
    dom.dataset.empty = hasSrc ? "false" : "true"
    captionToggle.hidden = hasCaption
    sizeGroup.querySelectorAll("[data-size]").forEach((btn) => {
      btn.dataset.active = Number(btn.dataset.size) === width ? "true" : "false"
    })
  }

  refresh(initialNode)

  return {
    dom,
    contentDOM,
    update(node) {
      if (node.type.name !== initialNode.type.name) return false
      refresh(node)
      return true
    },
    selectNode() {
      dom.classList.add("nl-image--selected")
    },
    deselectNode() {
      dom.classList.remove("nl-image--selected")
    },
    ignoreMutation(mutation) {
      return !contentDOM.contains(mutation.target)
    },
  }
}

// Drag handle that reports pointer deltas while held. The callback receives
// `{ delta, startWidth, pointerEvent }` so callers can convert the delta into
// the appropriate model-level attr update.
export function buildResizeHandle({ direction = "right", onResize, getStartWidth }) {
  const handle = document.createElement("div")
  handle.className = `nl-resize-handle nl-resize-handle--${direction}`
  handle.contentEditable = "false"
  handle.style.pointerEvents = "auto"
  handle.setAttribute("aria-label", "Drag to resize")
  handle.title = "Drag to resize"

  handle.addEventListener("mousedown", (event) => {
    event.preventDefault()
    event.stopPropagation()
    const startX = event.clientX
    const startWidth = typeof getStartWidth === "function" ? getStartWidth() : 100
    let raf = null
    let lastEvent = event
    document.body.style.cursor = "ew-resize"
    document.body.style.userSelect = "none"

    const tick = () => {
      raf = null
      const delta = direction === "left"
        ? -(lastEvent.clientX - startX)
        : (lastEvent.clientX - startX)
      onResize({ delta, startWidth, pointerEvent: lastEvent })
    }
    const onMove = (e) => {
      lastEvent = e
      if (raf == null) raf = requestAnimationFrame(tick)
    }
    const onUp = () => {
      if (raf != null) cancelAnimationFrame(raf)
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
  })

  return handle
}

export function buildSizeGroup(options, onPick) {
  const group = document.createElement("div")
  group.className = "nl-size-group"
  group.contentEditable = "false"
  group.style.pointerEvents = "auto"
  options.forEach(({ value, label, title }) => {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "nl-size-group__btn"
    btn.dataset.size = String(value)
    btn.title = title
    btn.setAttribute("aria-label", title)
    btn.textContent = label
    btn.contentEditable = "false"
    btn.addEventListener("mousedown", (event) => event.preventDefault())
    btn.addEventListener("click", (event) => {
      event.preventDefault()
      onPick(value)
    })
    group.appendChild(btn)
  })
  return group
}
